import axios from 'axios';
import type {
  TrackedEntityListItem,
  RevisionTimelineItem,
  RevisionSnapshotResponse,
  RevisionDiffResponse,
  SampleEntitySummary,
  CursorResponse,
} from '../types/changeHistoryViewer';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Bearer 토큰 부착
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const changeHistoryViewerApi = {
  /**
   * GET /change-history-viewer/entities
   * 추적 엔티티 목록 (커서 페이징)
   */
  listEntities: (params: {
    keyword?: string;
    status?: string;
    cursor?: string;
    size?: number;
  }): Promise<CursorResponse<TrackedEntityListItem>> => {
    const sp = new URLSearchParams();
    if (params.keyword) sp.set('keyword', params.keyword);
    if (params.status) sp.set('status', params.status);
    if (params.cursor) sp.set('cursor', params.cursor);
    if (params.size !== undefined) sp.set('size', String(params.size));
    return apiClient
      .get<unknown, { data: CursorResponse<TrackedEntityListItem> }>(
        `/change-history-viewer/entities?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /**
   * GET /change-history-viewer/timeline
   * revision 타임라인 (커서 페이징, entityId 필수)
   */
  getTimeline: (params: {
    entityId: number;
    cursor?: string;
    size?: number;
  }): Promise<CursorResponse<RevisionTimelineItem>> => {
    const sp = new URLSearchParams();
    sp.set('entityId', String(params.entityId));
    if (params.cursor) sp.set('cursor', params.cursor);
    if (params.size !== undefined) sp.set('size', String(params.size));
    return apiClient
      .get<unknown, { data: CursorResponse<RevisionTimelineItem> }>(
        `/change-history-viewer/timeline?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /**
   * GET /change-history-viewer/revisions/{rev}?entityId=...
   * 단일 revision 스냅샷
   */
  getRevisionSnapshot: (params: {
    rev: number;
    entityId: number;
  }): Promise<RevisionSnapshotResponse> => {
    return apiClient
      .get<unknown, { data: RevisionSnapshotResponse }>(
        `/change-history-viewer/revisions/${params.rev}?entityId=${params.entityId}`
      )
      .then((res) => res.data);
  },

  /**
   * GET /change-history-viewer/diff?entityId=...&from=...&to=...
   * 두 revision 간 before/after diff
   */
  getDiff: (params: {
    entityId: number;
    from: number;
    to: number;
  }): Promise<RevisionDiffResponse> => {
    const sp = new URLSearchParams();
    sp.set('entityId', String(params.entityId));
    sp.set('from', String(params.from));
    sp.set('to', String(params.to));
    return apiClient
      .get<unknown, { data: RevisionDiffResponse }>(
        `/change-history-viewer/diff?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /**
   * GET /change-history-viewer/sample-entities
   * 시연 엔티티 요약 카탈로그
   */
  getSampleEntities: (): Promise<SampleEntitySummary[]> => {
    return apiClient
      .get<unknown, { data: SampleEntitySummary[] }>(`/change-history-viewer/sample-entities`)
      .then((res) => res.data);
  },
};
